import { LAG_MIN_MEMBERS, type PortGroup, type PortGroupInput } from './schemas/port-group.ts';

// The rules and the words of a LAG or a mirror (ROADMAP C5), shared by the
// form (which offers only valid choices), the Worker (which refuses the rest),
// and the displays (the device page, the trace, and the topology). Pure: no
// database, no DOM.

/** The groups configured on one device, source order kept. */
export function portGroupsOn(groups: readonly PortGroup[], itemId: string): PortGroup[] {
  return groups.filter((group) => group.itemId === itemId);
}

/** A group's own name, or a made-up one from its kind when it was left unlabelled. */
export function portGroupName(group: Pick<PortGroup, 'kind' | 'label'>): string {
  if (group.label !== '') {
    return group.label;
  }
  return group.kind === 'lag' ? 'LAG' : 'Mirror';
}

/** "1, 2, 3" from a list of port numbers, in the order given. */
function portList(ports: readonly number[]): string {
  return ports.map(String).join(', ');
}

/**
 * A group on one line: a bond as its name and the ports it bundles, a mirror
 * as its name, its source ports, and the monitor port they copy to.
 */
export function describePortGroup(group: PortGroup): string {
  const name = portGroupName(group);
  if (group.kind === 'lag') {
    return `${name} · ports ${portList(group.memberPorts)}`;
  }
  const to = group.monitorPort === null ? '' : ` → port ${String(group.monitorPort)}`;
  return `${name} · from ${portList(group.memberPorts)}${to}`;
}

/**
 * What a device's port does in any group it belongs to — "LAG ag0", "Mirror
 * source", or "Monitor port" — so a link on that port reads as part of one
 * logical link and a mirror is labelled. '' when the port is in no group.
 */
export function portGroupRole(groups: readonly PortGroup[], itemId: string, port: number): string {
  for (const group of portGroupsOn(groups, itemId)) {
    if (group.kind === 'lag' && group.memberPorts.includes(port)) {
      return `LAG ${portGroupName(group)}`;
    }
    if (group.kind === 'mirror') {
      if (group.monitorPort === port) {
        return `Monitor port (${portGroupName(group)})`;
      }
      if (group.memberPorts.includes(port)) {
        return `Mirror source (${portGroupName(group)})`;
      }
    }
  }
  return '';
}

/** Every port a group holds: its members, plus a mirror's monitor port. */
function portsOf(group: Pick<PortGroup, 'memberPorts' | 'monitorPort'>): number[] {
  return group.monitorPort === null
    ? [...group.memberPorts]
    : [...group.memberPorts, group.monitorPort];
}

/**
 * The reason a group cannot be saved, or null when it can. A bond needs at
 * least two ports and no monitor; a mirror needs a monitor port distinct from
 * its sources. Every port must be a real one on the device, named once, and
 * not already held by another of the device's groups.
 */
export function portGroupProblem(
  input: PortGroupInput,
  capacity: number | null,
  existing: readonly PortGroup[],
): string | null {
  if (capacity === null) {
    return 'That device has no numbered ports';
  }
  const ports = portsOf(input);
  if (new Set(ports).size !== ports.length) {
    return 'A port cannot be named twice in one group';
  }
  for (const port of ports) {
    if (port > capacity) {
      return `That device has only ${String(capacity)} ports`;
    }
  }
  if (input.kind === 'lag') {
    if (input.monitorPort !== null) {
      return 'A link aggregation group has no monitor port';
    }
    if (input.memberPorts.length < LAG_MIN_MEMBERS) {
      return `A bond needs at least ${String(LAG_MIN_MEMBERS)} ports`;
    }
  } else {
    if (input.monitorPort === null) {
      return 'Choose the monitor port the mirror copies to';
    }
    if (input.memberPorts.length === 0) {
      return 'Choose at least one source port to mirror';
    }
  }
  const claimed = new Set(
    existing.filter((group) => group.itemId === input.itemId).flatMap((group) => portsOf(group)),
  );
  for (const port of ports) {
    if (claimed.has(port)) {
      return `Port ${String(port)} is already in another group on this device`;
    }
  }
  return null;
}
